
import {
  computed, defineComponent, Ref, SetupContext, ref,
} from 'vue';

import Tooltip from 'primevue/tooltip';

import StatsWidgetDataRender from '@/components/stats-builder/widgets/render/StatsWidgetDataRender.vue';
import StatsWidgetTitleRender from '@/components/stats-builder/widgets/render/StatsWidgetTitleRender.vue';
import { StatsBuilderSelectedWidget, StatsBuilderChoosedWidgetForDrag } from '@/types/stats-editor-types';
import { useStore } from '@/store';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'StatsBuilderSubColItem',

  components: {
    StatsWidgetDataRender,
    StatsWidgetTitleRender,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    element: {
      type: Object,
      required: true,
    },

    index: {
      type: Number,
      required: true,
    },

    rowId: {
      type: String,
      required: true,
    },

    columnData: {
      type: Object,
      required: true,
    },

    rowParentId: {
      type: String,
      required: true,
    },

    columnParentId: {
      type: String,
      required: true,
    },

    panelId: {
      type: String,
      required: true,
    },
  },

  emits: ['edit-widget', 'remove-widget'],

  setup(props, { emit }: SetupContext) {
    const { t } = useI18n();
    const store = useStore();
    const selectedWidgetState: Ref<StatsBuilderSelectedWidget | null> = computed(
      () => store.getters['statsEditor/getSelectedWidget'],
    );

    const hovered = ref(false);

    const panelDataHasWidgetIndex = (
      panelData: StatsBuilderSelectedWidget['location']['panelData'],
    ): panelData is NonNullable<StatsBuilderSelectedWidget['location']['panelData']> & { widgetIndex: number } => (
      !!panelData && typeof (panelData as Record<string, unknown>).widgetIndex === 'number'
    );

    const isActiveWidget = computed(() => {
      if (!selectedWidgetState.value) {
        return false;
      }
      const { location } = selectedWidgetState.value;
      if (location.rowId !== props.rowParentId || location.columnId !== props.columnParentId) {
        return false;
      }
      if (!panelDataHasWidgetIndex(location.panelData)) {
        return false;
      }
      const { panelData } = location;
      return panelData.panelId === props.panelId
        && panelData.subRowId === props.rowId
        && panelData.subColumnId === props.columnData.id
        && panelData.widgetIndex === props.index;
    });

    const choosedWidgetForDrag = computed<StatsBuilderChoosedWidgetForDrag | null>(() => store.getters['statsEditor/getChoosedWidgetForDrag']);

    const isChoosedForDrag = computed(() => {
      if (!choosedWidgetForDrag.value) {
        return false;
      }
      const continueCheck = choosedWidgetForDrag.value.rowId === props.rowParentId
        && choosedWidgetForDrag.value.columnId === props.columnParentId && choosedWidgetForDrag.value.index === props.index;
      if (continueCheck && panelDataHasWidgetIndex(choosedWidgetForDrag.value.panelData)) {
        const { panelData } = choosedWidgetForDrag.value;
        return panelData.panelId === props.panelId && panelData.subRowId === props.rowId
          && panelData.subColumnId === props.columnData.id && panelData.widgetIndex === props.index;
      }
      return false;
    });

    const editWidget = () => {
      emit('edit-widget', props.index);
    };

    const removeWidget = () => {
      emit('remove-widget', props.index);
    };

    const onMouseOver = () => {
      hovered.value = true;
    };
    const onMouseLeave = () => {
      hovered.value = false;
    };

    return {
      t,
      selectedWidgetState,
      hovered,
      isChoosedForDrag,
      isActiveWidget,

      editWidget,
      removeWidget,
      onMouseOver,
      onMouseLeave,
    };
  },
});
